import { useState, useEffect, Fragment, useMemo, useCallback } from 'react';
import {
    UserPlusIcon,
} from '@heroicons/react/20/solid';
import { Dialog, Transition, Switch } from '@headlessui/react'
import ExcelImport, { ImportSummary, MissingEmails, importErrorText, buildMissingItems, applyEmailFixes, unresolvedMissing, typedEmails } from '../../components/admin/excelimport.component';

//services
import MeetingAdminService from "../../services/meeting.admin.service";
import UserAdminService from '../../services/user.admin.service';
import { toast } from 'react-toastify';

//components
import Spinner from '../../components/ui/spinner.component';

//hooks
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// Columns the import looks for; match holds header names that select a column automatically.
const USER_FIELDS = [
    { key: 'name', label: 'Naam', required: true, match: ['stemhouder', 'contact naam', 'naam', 'name', 'contact'] },
    { key: 'email', label: 'E-mailadres', required: true, match: ['email', 'e-mail', 'e-mailadres', 'mail'] },
    { key: 'telephone', label: 'GSM', required: false, match: ['gsm', 'mobiel', 'mobile', 'telefoon', 'tel', 'phone'] },
    { key: 'telephone2', label: 'Telefoon', required: false, match: ['tel', 'telefoon', 'phone'] },
    { key: 'language', label: 'Taal', required: false, match: ['taal', 'language', 'lang'] },
];

export default function AdminUsersView() {
    // Existing state variables for modals and forms
    const [modalOpen, setModalOpen] = useState(false);
    const [excelModalOpen, setExcelModalOpen] = useState(false)
    const [uploading, setUploading] = useState(false);
    const [importRows, setImportRows] = useState(null);
    const [importStep, setImportStep] = useState('file'); // file, preview, done
    const [importResult, setImportResult] = useState(null);
    const [missingItems, setMissingItems] = useState([]); // names without a usable e-mail in the last check
    const [emailFixes, setEmailFixes] = useState({});
    const [invalidFixes, setInvalidFixes] = useState([]);
    const [firstname, setFirstname] = useState("");
    const [lastname, setLastname] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [language, setLanguage] = useState("nl");
    const [admin, setAdmin] = useState(false);

    // New state for infinite scroll
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState('users');
    const [sortColumn, setSortColumn] = useState('firstname');
    const [sortDirection, setSortDirection] = useState('asc');
    const [totalUsers, setTotalUsers] = useState(0);

    // Infinite scroll hook
    const { loading, setLoading, hasMore, setHasMore, setupScrollListener, resetScroll } = useInfiniteScroll();

    // Fetch users function (without dependencies to prevent loops)
    const fetchUsers = useCallback(async (page = 1, reset = false, searchOverride = null, sortByOverride = null, sortDirOverride = null, filterOverride = null) => {
        if (loading) return;
        
        setLoading(true);
        try {
            const response = await UserAdminService.getUsers({
                page,
                per_page: 50,
                search: searchOverride !== null ? searchOverride : searchTerm,
                sort_by: sortByOverride !== null ? sortByOverride : sortColumn,
                sort_dir: sortDirOverride !== null ? sortDirOverride : sortDirection,
                filter: filterOverride !== null ? filterOverride : userTypeFilter
            });

            const { users: newUsers, pagination } = response.data;
            
            if (reset) {
                setUsers(newUsers);
                setCurrentPage(1);
            } else {
                setUsers(prev => page === 1 ? newUsers : [...prev, ...newUsers]);
            }
            
            setHasMore(pagination.has_next);
            setTotalUsers(pagination.total);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Er is een fout opgetreden bij het laden van gebruikers.');
        } finally {
            setLoading(false);
        }
    }, [loading]);

    // Load more users for infinite scroll
    const loadMoreUsers = useCallback(() => {
        if (hasMore && !loading) {
            fetchUsers(currentPage + 1);
        }
    }, [fetchUsers, hasMore, loading, currentPage]);

    // Handle search input change (just updates state, doesn't trigger search)
    const handleSearchInput = useCallback((value) => {
        setSearchTerm(value);
    }, []);

    // Handle search submit (button click or Enter key)
    const handleSearchSubmit = useCallback(() => {
        resetScroll();
        setCurrentPage(1);
        fetchUsers(1, true, searchTerm);
    }, [searchTerm, resetScroll, fetchUsers]);

    // Handle sorting
    const handleSort = useCallback((column) => {
        const newDirection = column === sortColumn && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortColumn(column);
        setSortDirection(newDirection);
        
        // Directly fetch with new sort parameters
        resetScroll();
        setCurrentPage(1);
        fetchUsers(1, true, null, column, newDirection);
    }, [sortColumn, sortDirection, resetScroll, fetchUsers]);

    // Handle filter change
    const handleFilterChange = useCallback((filter) => {
        setUserTypeFilter(filter);
        
        // Directly fetch with new filter
        resetScroll();
        setCurrentPage(1);
        fetchUsers(1, true, null, null, null, filter);
    }, [resetScroll, fetchUsers]);

    // Initial load
    useEffect(() => {
        document.title = "Assemblo - Admin - Gebruikers";
        fetchUsers(1, true);
    }, []); // Only run once on mount

    // Setup scroll listener
    useEffect(() => {
        const cleanup = setupScrollListener(loadMoreUsers);
        return cleanup;
    }, [loadMoreUsers]);


    function addUser() {
        if (firstname && lastname && email && phone && language) {
            UserAdminService.addUser(
                {
                    firstname: firstname,
                    lastname: lastname,
                    email: email,
                    telephone: phone,
                    language: language,
                    admin: admin
                }
            )
                .then(response => {
                    // Add user to the beginning of the list
                    setUsers(prev => [response.data, ...prev]);
                    setTotalUsers(prev => prev + 1);
                    closeModal();
                    toast.success("Gebruiker is toegevoegd.");
                })
                .catch(err => {
                    console.log(err);
                    toast.error("Er is een fout opgetreden bij het toevoegen van de gebruiker.");
                }
                );

        } else {
            toast.error("Vul alle velden in.");
        }
    }

    function deleteUser(id) {
        UserAdminService.deleteUser(id)
            .then(response => {
                setUsers(prev => prev.filter(user => user.id !== id));
                setTotalUsers(prev => prev - 1);
                toast.success("Gebruiker is verwijderd.");
            })
            .catch(err => {
                console.log(err);
                toast.error("Er is een fout opgetreden bij het verwijderen van de gebruiker.");
            }
            );
    }

    function closeModal() {
        setModalOpen(false);
        setAdmin(false);
        setFirstname("");
        setLastname("");
        setEmail("");
        setPhone("");
    }

    function checkImport() {
        if (!importRows || importRows.length === 0) {
            toast.error("Er is geen bestand geselecteerd of niet alle kolommen zijn gekozen.");
            return;
        }
        setUploading(true);
        UserAdminService.uploadExcel(importRows, true)
            .then(response => {
                setImportResult(response.data);
                setMissingItems(buildMissingItems(response.data));
                setEmailFixes({});
                setInvalidFixes([]);
                setImportStep('preview');
            })
            .catch(err => {
                console.log(err);
                toast.error(importErrorText(err));
            })
            .finally(() => setUploading(false));
    }

    // Writes the typed e-mail addresses into the rows, checks them with a dry
    // run and only then imports.
    function runImport() {
        if (unresolvedMissing(missingItems, emailFixes).length > 0) {
            toast.error("Vul de ontbrekende e-mailadressen in of vink Overslaan aan.");
            return;
        }
        const rows = applyEmailFixes(importRows, missingItems, emailFixes);
        setUploading(true);
        UserAdminService.uploadExcel(rows, true)
            .then(check => {
                const typed = typedEmails(emailFixes);
                const rejected = check.data.invalid_email.map(item => item.email).filter(email => typed.includes(email));
                if (rejected.length > 0) {
                    setImportResult(check.data);
                    setInvalidFixes(rejected);
                    toast.error("Een ingevuld e-mailadres is ongeldig.");
                    return null;
                }
                return UserAdminService.uploadExcel(rows, false);
            })
            .then(response => {
                if (!response) return;
                setImportResult(response.data);
                setImportStep('done');
                toast.success("Gebruikers zijn toegevoegd.");
                resetScroll();
                setCurrentPage(1);
                fetchUsers(1, true);
            })
            .catch(err => {
                console.log(err);
                toast.error(importErrorText(err));
            })
            .finally(() => setUploading(false));
    }

    function cancelExcelUpload() {
        setExcelModalOpen(false);
        setImportRows(null);
        setImportStep('file');
        setImportResult(null);
        setMissingItems([]);
        setEmailFixes({});
        setInvalidFixes([]);
    }

    function updateMail(mail, id, update) {
        setUsers(users => users.map(user => {
            if (user.id === id) {
                user.email = mail;
            }
            return user;
        }));
        if (update) {
            UserAdminService.updateMail(id, mail)
                .then(response => {
                    toast.success("E-mailadres is aangepast.");
                })
                .catch(err => {
                    console.log(err);
                    toast.error("Er is iets misgegaan bij het aanpassen van het e-mailadres.");
                    // Revert the change on error
                    setUsers(users => users.map(user => {
                        if (user.id === id) {
                            // You might want to store the original email to revert to
                            return user;
                        }
                        return user;
                    }));
                }
                );
        }
    }


    return (
        <main className="flex-1">
            <div className="py-6">
                <div className="mx-auto px-4 sm:px-6 lg:px-8 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center">
                        <h1 className="text-2xl font-semibold text-gray-900">Gebruikers</h1>
                        {loading && (
                            <div className="ml-3">
                                <Spinner size="small" />
                            </div>
                        )}
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-4">
                        <button
                            type="button"
                            onClick={(e) => setExcelModalOpen(true)}
                            className="inline-flex items-center rounded-md border border-transparent bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 mr-2"
                        >
                            <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                            Excel uploaden
                        </button>

                        <button
                            type="button"
                            onClick={(e) => setModalOpen(true)}
                            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                            <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                            Nieuwe gebruiker toevoegen
                        </button>
                    </div>
                </div>
                <div className="mx-auto px-4 sm:px-6 md:px-8">
                    <p>Hieronder een lijst van alle Assemblo-gebruikers.</p>
                    <br />

                    {/* table */}
                    <div className="">
                        <div className="mt-8 flex flex-col">
                            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                        <div className="p-4">
                                            <div className="flex space-x-2">
                                                <input
                                                    type="text"
                                                    placeholder="Zoeken..."
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearchInput(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                                                    className="flex-1 shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                                                />
                                                <button
                                                    onClick={handleSearchSubmit}
                                                    disabled={loading}
                                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                                >
                                                    Zoeken
                                                </button>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between">
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleFilterChange('users')}
                                                        disabled={loading}
                                                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                            userTypeFilter === 'users'
                                                                ? 'bg-indigo-100 text-indigo-700'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        Investeerders
                                                    </button>
                                                    <button
                                                        onClick={() => handleFilterChange('medewerkers')}
                                                        disabled={loading}
                                                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                            userTypeFilter === 'medewerkers'
                                                                ? 'bg-indigo-100 text-indigo-700'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        MedeMerkers
                                                    </button>
                                                    <button
                                                        onClick={() => handleFilterChange('admins')}
                                                        disabled={loading}
                                                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                            userTypeFilter === 'admins'
                                                                ? 'bg-indigo-100 text-indigo-700'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        Administrators
                                                    </button>
                                                    <button
                                                        onClick={() => handleFilterChange('all')}
                                                        disabled={loading}
                                                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                            userTypeFilter === 'all'
                                                                ? 'bg-indigo-100 text-indigo-700'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        Alle gebruikers
                                                    </button>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {totalUsers} gebruiker{totalUsers !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th
                                                        scope="col"
                                                        className={`py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer transition-colors ${loading ? 'opacity-50' : 'hover:bg-gray-200'}`}
                                                        onClick={() => !loading && handleSort('firstname')}
                                                    >
                                                        Naam {sortColumn === 'firstname' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer transition-colors ${loading ? 'opacity-50' : 'hover:bg-gray-200'}`}
                                                        onClick={() => !loading && handleSort('email')}
                                                    >
                                                        E-mail {sortColumn === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer transition-colors ${loading ? 'opacity-50' : 'hover:bg-gray-200'}`}
                                                        onClick={() => !loading && handleSort('telephone')}
                                                    >
                                                        Telefoon {sortColumn === 'telephone' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer transition-colors ${loading ? 'opacity-50' : 'hover:bg-gray-200'}`}
                                                        onClick={() => !loading && handleSort('lang')}
                                                    >
                                                        Taal {sortColumn === 'lang' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer transition-colors ${loading ? 'opacity-50' : 'hover:bg-gray-200'}`}
                                                        onClick={() => !loading && handleSort('admin')}
                                                    >
                                                        Rol {sortColumn === 'admin' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                    </th>
                                                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                                        <span className="sr-only">Edit</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white">
                                                {users.map((person, personIdx) => (
                                                    <tr key={person.id} className={personIdx % 2 === 0 ? undefined : 'bg-gray-50'}>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {person.firstname} {person.lastname}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            <input
                                                                className="w-full border-none bg-transparent rounded-md"
                                                                type="text"
                                                                value={person.email}
                                                                onChange={(e) => updateMail(e.target.value, person.id, false)}
                                                                onBlur={(e) => updateMail(e.target.value, person.id, true)}
                                                                onKeyDown={(e) => e.key === 'Enter' && updateMail(e.target.value, person.id, true)}
                                                            />
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{person.telephone}</td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {person.lang === "nl" ? "Nederlands" : person.lang === "fr" ? "Frans" : person.lang === "en" ? "Engels" : "Onbekend"}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {person.admin ? "Administrator" : "Investeerder"}
                                                        </td>
                                                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                            <a onClick={() => deleteUser(person.id)} className="text-indigo-600 hover:text-indigo-900 cursor-pointer select-none">
                                                                Verwijderen<span className="sr-only">, {person.firstname} {person.lastname}</span>
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {!loading && users.length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-8 text-gray-500">
                                                            Geen gebruikers gevonden
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* MODAL ADD USER */}
            <Transition.Root show={modalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setModalOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                    <div>

                                        <div className="mt-1">
                                            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                                Gebruiker toevoegen
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Vul onderstaande velden in om een nieuwe gebruiker toe te voegen.
                                                    De gebruiker ontvangt hiervan geen notificatie, maar kan inloggen met de magische link.
                                                </p>
                                            </div>
                                            <div>
                                                <form className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
                                                    <div>
                                                        <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">
                                                            Voornaam
                                                        </label>
                                                        <div className="mt-1">
                                                            <input

                                                                type="text"
                                                                name="firstname"
                                                                id="firstname"
                                                                autoComplete="given-name"
                                                                className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                                                                onChange={e => (setFirstname(e.target.value))}
                                                                value={firstname}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">
                                                            Achternaam
                                                        </label>
                                                        <div className="mt-1">
                                                            <input

                                                                type="text"
                                                                name="firstname"
                                                                id="firstname"
                                                                autoComplete="given-name"
                                                                className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                                                                onChange={e => (setLastname(e.target.value))}
                                                                value={lastname}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">
                                                            E-mailadres
                                                        </label>
                                                        <div className="mt-1">
                                                            <input
                                                                type="text"
                                                                name="firstname"
                                                                id="firstname"
                                                                autoComplete="given-name"
                                                                className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                                                                onChange={e => (setEmail(e.target.value))}
                                                                value={email}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">
                                                            Telefoonnummer
                                                        </label>
                                                        <div className="mt-1">
                                                            <input
                                                                type="text"
                                                                name="firstname"
                                                                id="firstname"
                                                                autoComplete="given-name"
                                                                className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                                                                onChange={e => (setPhone(e.target.value))}
                                                                value={phone}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">
                                                            Taal
                                                        </label>
                                                        <select
                                                            id="language"
                                                            name="language"
                                                            autoComplete="language"
                                                            className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                                                            onChange={e => (setLanguage(e.target.value))}
                                                            value={language}
                                                        >
                                                            <option value="nl">Nederlands</option>
                                                            <option value="fr">Frans</option>
                                                            <option value="en">Engels</option>
                                                        </select>
                                                    </div>
                                                    <Switch.Group as="div" className="flex items-center justify-between col-span-2">
                                                        <span className="flex flex-grow flex-col">
                                                            <Switch.Label as="span" className="text-sm font-medium text-gray-900" passive>
                                                                Administrator
                                                            </Switch.Label>
                                                            <Switch.Description as="span" className="text-sm text-gray-500">
                                                                Pas op, deze gebruiker krijgt toegang tot alle gegevens.
                                                            </Switch.Description>
                                                        </span>
                                                        <Switch
                                                            checked={admin}
                                                            onChange={setAdmin}
                                                            className={classNames(
                                                                admin ? 'bg-indigo-600' : 'bg-gray-200',
                                                                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                                                            )}
                                                        >
                                                            <span
                                                                aria-hidden="true"
                                                                className={classNames(
                                                                    admin ? 'translate-x-5' : 'translate-x-0',
                                                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                                )}
                                                            />
                                                        </Switch>
                                                    </Switch.Group>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-12 sm:mt-12 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                                            onClick={() => addUser()}
                                        >
                                            Aanmaken
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                                            onClick={() => closeModal()}
                                        >
                                            Annuleer
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* MODAL EXCEL UPLOAD */}
            <Transition.Root show={excelModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={cancelExcelUpload}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                            Gebruikers importeren van Excel
                                        </Dialog.Title>
                                        <div className={importStep === 'file' ? 'mt-2' : 'hidden'}>
                                            <p className="text-sm text-gray-500">
                                                Laad een .xlsx bestand in waarvan de eerste rij de header is.
                                                Rijen met hetzelfde e-mailadres worden samengevoegd en gebruikers die al bestaan blijven ongewijzigd.
                                            </p>
                                            <div className="mt-4">
                                                <ExcelImport fields={USER_FIELDS} onChange={setImportRows} />
                                            </div>
                                        </div>
                                        {importStep !== 'file' ? (
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    {importStep === 'preview'
                                                        ? 'Controleer het overzicht. Er is nog niets opgeslagen.'
                                                        : 'De import is uitgevoerd.'}
                                                </p>
                                                <div className="mt-4">
                                                    <ImportSummary result={importResult} showSkipped={importStep === 'done'} />
                                                </div>
                                                {importStep === 'preview' ? (
                                                    <MissingEmails items={missingItems} fixes={emailFixes} onChange={setEmailFixes} invalidEmails={invalidFixes} />
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                    {uploading ?
                                        <div className="mt-2 mb-4">
                                            <Spinner />
                                        </div>
                                        :
                                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                            {importStep === 'file' ? (
                                                <button
                                                    type="button"
                                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                                                    onClick={() => checkImport()}
                                                    disabled={!importRows}
                                                >
                                                    Controleren
                                                </button>
                                            ) : null}
                                            {importStep === 'preview' ? (
                                                <button
                                                    type="button"
                                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                                                    onClick={() => runImport()}
                                                    disabled={unresolvedMissing(missingItems, emailFixes).length > 0}
                                                >
                                                    Importeren
                                                </button>
                                            ) : null}
                                            {importStep === 'preview' ? (
                                                <button
                                                    type="button"
                                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                                    onClick={() => setImportStep('file')}
                                                >
                                                    Terug
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                                onClick={() => cancelExcelUpload()}
                                            >
                                                Sluiten
                                            </button>
                                        </div>}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </main>
    )
}